import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateDoctorUserFormData, createDoctorUserSchema } from "@/lib/validations/auth";
import AppointmentSchedule from "./AppointmentSchedule";
import AppointmentInformation from "./AppointmentInformation";
import AddressInformation from "./AddressInformation";
import ContactInformation from "./ContactInformation";
import { userService } from "@/lib/services/userService";
import { DoctorOverview, ErrorMessagesType } from "@/types";

interface CreateDoctorAccountProps {
  isOpen: boolean;
  onClose: () => void;
  btnRef: React.RefObject<HTMLButtonElement>;
  refreshDoctors: () => void;
  editingUser?: DoctorOverview | null;
}


const CreateDoctorAccount = ({ isOpen, onClose, btnRef, refreshDoctors, editingUser = null }: CreateDoctorAccountProps) => {
  const [errorMessages, setErrorMessages] = useState<ErrorMessagesType>({});
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateDoctorUserFormData>({
    resolver: zodResolver(createDoctorUserSchema as any),
  });

  const onSubmit = async (data: CreateDoctorUserFormData) => {
    console.log("Form Data:", data);
    let res;
    if(editingUser) {
      res = await userService.updateDoctor(data, editingUser.id.toString());
    } else {
      res = await userService.createDoctor(data);
    }
    
    setErrorMessages({});
    if (res.status_code == 200 || res.status_code == 201) {
      refreshDoctors();
      reset();
      onClose();
    } else if(res.status_code == 409) {
      console.log("Error Response:", res);
      if(res.errors && res.errors["user_email"]) {
        setErrorMessages(prev => ({ ...prev, user_email: { message:"This email is already registered."} }));
      }
      if(res.errors && res.errors["user_login"]) {
        setErrorMessages(prev => ({ ...prev, user_login: { message:"This username is already registered."} }));
      }
      if(res.errors && res.errors["user_phone_number"]) {
        setErrorMessages(prev => ({ ...prev, user_phone_number: { message:"This phone number is already registered."} }));
      }
      if(res.errors && res.errors["medical_license_number"]) {
        setErrorMessages(prev => ({ ...prev, medical_license_number: { message:"This medical license number is already registered."} }));
      }
    }
  };

  const onError = (errors: any) => {
    console.log("Form Errors:", errors);
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={btnRef}
      size={{
        base:"full",
        md:"md",
        lg: "lg",
        "2xl":"xl",
      }}
    >
      <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(1px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{editingUser ? "Edit Doctor" : "New Doctor"}</DrawerHeader>
        <DrawerBody p={0}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <VStack spacing={0} align="stretch">
              <Heading size="sm" bg="gray.200" p={2}>
                Contact Information
              </Heading>
              <Box px={3} py={2}>
                <ContactInformation register={register}  setValue={setValue} errors={{...errors, ...errorMessages}} control={control} editingUser={editingUser} />
              </Box>
              <Heading size="sm" bg="gray.200" p={2}>
                Address Information
              </Heading>
              <Box px={3} py={2}>
                <AddressInformation register={register}  setValue={setValue} errors={errors} editingUser={editingUser} />
              </Box>
              <Heading size="sm" bg="gray.200" p={2}>
                Appointment Schedule
              </Heading>
              <Box px={3} py={2}>
                <AppointmentSchedule register={register} watch={watch} setValue={setValue} editingUser={editingUser} />
              </Box>
              <Heading size="sm" bg="gray.200" p={2}>
                Appointment Information
              </Heading>
              <Box px={3} py={2}>
                <AppointmentInformation register={register}  setValue={setValue} errors={errors} editingUser={editingUser} />
              </Box>
            </VStack>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button bg="red.500" color="white" onClick={handleSubmit(onSubmit, onError)}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default CreateDoctorAccount;